import { t } from 'state/intl/translations';
import {
  AppOffer,
  AppOfferListItem,
  isAppOffer,
  isAppOfferGroup,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';

/**
 * The offerEngineId route param is created only after offer detail has settled
 * as unavailable. The recovery query can later resolve that offer without a BE
 * inline message, so preserve the route's unavailable decision with FE copy.
 */
export function markOfferUnavailableForRecovery(recoveredOffer?: AppOffer): AppOffer | undefined {
  if (!recoveredOffer || recoveredOffer.inlineAlertMessage) {
    return recoveredOffer;
  }

  return {
    ...recoveredOffer,
    inlineAlertMessage: t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }),
  };
}

function isSameOffer(offer: AppOffer, highlightedOffer: AppOffer): boolean {
  return offer.loyaltyEngineId === highlightedOffer.loyaltyEngineId;
}

/**
 * Mirrors menu unavailable recovery: inject the catalog-backed unavailable
 * item above the store-scoped list and remove its original occurrence.
 */
export function buildUnavailableOfferRecoveryList(
  offerListItems: AppOfferListItem[],
  highlightedOffer?: AppOffer
): AppOfferListItem[] {
  if (!highlightedOffer?.inlineAlertMessage) {
    return offerListItems;
  }

  const remainingItems: AppOfferListItem[] = [];

  for (const item of offerListItems) {
    if (isAppOffer(item)) {
      if (!isSameOffer(item, highlightedOffer)) {
        remainingItems.push(item);
      }
      continue;
    }

    if (isAppOfferGroup(item)) {
      const offers = item.offers.filter(offer => !isSameOffer(offer, highlightedOffer));
      if (offers.length > 0) {
        remainingItems.push(offers.length === item.offers.length ? item : { ...item, offers });
      }
      continue;
    }

    if (isAppPersonalizedOfferGroup(item)) {
      const offers = item.offers.filter(offer => !isSameOffer(offer, highlightedOffer));
      if (offers.length > 0) {
        remainingItems.push(offers.length === item.offers.length ? item : { ...item, offers });
      }
      continue;
    }

    remainingItems.push(item);
  }

  return [highlightedOffer, ...remainingItems];
}

export function getOtherAvailableOffersHeaderIndex(
  offerListItems: AppOfferListItem[],
  highlightedOffer?: AppOffer
): number {
  if (!highlightedOffer?.inlineAlertMessage || offerListItems[0] !== highlightedOffer) {
    return -1;
  }

  return offerListItems.findIndex(item => !item.inlineAlertMessage);
}
